import React, { useCallback, useEffect, useMemo, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Drawer from "antd/es/drawer";
import List from "antd/es/list";
import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Dated from "@mapmycustomers/shared/types/base/Dated";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import Named from "@mapmycustomers/shared/types/base/Named";
import User from "@mapmycustomers/shared/types/User";
import { SearchField } from "@mapmycustomers/ui";

import useAnalytics from "@app/util/contexts/useAnalytics";
import getFilteredSavedFilters from "@app/util/getFilteredSavedFilters";

import ActionBar from "./ActionBar";
import SortField from "./enum/SortField";
import styles from "./ListDrawer.module.scss";
import ListDrawerItem from "./ListDrawerItem";

export interface ListDrawerProps<T> {
  deleteConfirmationText: string;
  icon: IconProp;
  information?: React.ReactNode;
  items: T[];
  onDelete?: (item: T) => void;
  onEdit?: (item: T) => void;
  onHide: () => void;
  onSelect?: (item: T) => void;
  paneTitle: string;
  rowDisabledTooltip?: React.ReactNode;
  rowEnabledGetter?: (item: T) => boolean;
  rowSubTitleGetter?: (item: T) => React.ReactNode;
  rowTitleGetter?: (item: T) => React.ReactNode;
  selectConfirmationConfirmText?: string;
  selectConfirmationText?: string;
  visible?: boolean;
}

interface Props<T> extends ListDrawerProps<T> {
  customFooter?: React.ReactNode;
  onChangeSortField?: (sortField: SortField) => void;
  onChangeSortOrder?: (sortOrder: SortOrder) => void;
  onSearch?: (query: string) => void;
  onSelectCreatorIds?: (creatorIds: User["id"][]) => void;
}

const ListDrawer = <T extends Identified & Named & Dated<Date>>({
  customFooter,
  deleteConfirmationText,
  icon,
  information,
  items,
  onChangeSortField,
  onChangeSortOrder,
  onDelete,
  onEdit,
  onHide,
  onSearch,
  onSelect,
  onSelectCreatorIds,
  paneTitle,
  rowDisabledTooltip,
  rowEnabledGetter,
  rowSubTitleGetter,
  rowTitleGetter = ({ name }) => name,
  selectConfirmationConfirmText,
  selectConfirmationText,
  visible,
}: Props<T>) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const [search, setSearch] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortField, setSortField] = useState<SortField>(SortField.UPDATED_AT);
  const [creators, setCreators] = useState<User["id"][]>([]);

  useEffect(() => {
    onSearch?.(search);
    onChangeSortOrder?.(sortOrder);
    onChangeSortField?.(sortField);
    onSelectCreatorIds?.(creators);
  }, [
    onSearch,
    search,
    sortField,
    onChangeSortOrder,
    sortOrder,
    onChangeSortField,
    onSelectCreatorIds,
    creators,
  ]);

  const preparedRows: T[] = useMemo(() => {
    if (customFooter) {
      return items;
    } else {
      const savedFilters = getFilteredSavedFilters(search, items, sortField, sortOrder, creators);
      return savedFilters;
    }
  }, [customFooter, creators, items, search, sortField, sortOrder]);

  const handleSelect = useCallback(
    (row: T) => {
      onSelect?.(row);
      onHide();
      analyticIssuer?.feature(["Select"], { ...row });
    },
    [analyticIssuer, onHide, onSelect]
  );

  const handleDelete = useCallback(
    (row: T) => {
      onDelete?.(row);
      analyticIssuer?.feature(["Delete"], { ...row });
    },
    [analyticIssuer, onDelete]
  );

  const handleEdit = useCallback(
    (row: T) => {
      onEdit?.(row);
      analyticIssuer?.feature(["Delete"], { ...row });
    },
    [analyticIssuer, onEdit]
  );

  const handleSearch = useCallback(
    (value: string) => {
      analyticIssuer?.clicked(["Search Filters"], { search: value });
      setSearch(value);
    },
    [analyticIssuer, setSearch]
  );

  return (
    <Drawer
      className={styles.container}
      closable
      footer={customFooter ?? undefined}
      onClose={onHide}
      open={visible}
      placement="right"
      title={
        <div className={styles.title}>
          <div className={styles.titleIconContainer}>
            <FontAwesomeIcon className={styles.titleIcon} icon={icon} />
          </div>
          {paneTitle}
        </div>
      }
    >
      <div>
        <SearchField
          className={styles.searchField}
          onChange={handleSearch}
          placeholder={intl.formatMessage({
            id: "listDrawer.search.placeholder",
            defaultMessage: "Type to search",
            description: "List drawer search placeholder",
          })}
          size="large"
        />
      </div>
      <ActionBar
        creatorIds={creators}
        onChangeCreators={setCreators}
        onChangeSortField={setSortField}
        onChangeSortOrder={setSortOrder}
        sort={{ field: sortField, order: sortOrder }}
      />
      {information}
      <List<T>
        dataSource={preparedRows}
        renderItem={(item) => (
          <ListDrawerItem<T>
            deleteConfirmationText={deleteConfirmationText}
            disabledTooltip={rowDisabledTooltip}
            enabledGetter={rowEnabledGetter}
            item={item}
            onDelete={onDelete ? handleDelete : undefined}
            onEdit={onEdit ? handleEdit : undefined}
            onSelect={handleSelect}
            selectConfirmationConfirmText={selectConfirmationConfirmText}
            selectConfirmationText={selectConfirmationText}
            subTitleGetter={rowSubTitleGetter}
            titleGetter={rowTitleGetter}
          />
        )}
      />
    </Drawer>
  );
};

export default ListDrawer;
